import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import {
  create_move_in_handover,
  update_handover,
  get_all_inspection_settings,
  add_inspection_settings,
  get_all_handovers,
  GET_CURRENCIES_URL,
  getCustomersURL,
  getUnitsForFacility,
  upload_handover_images,
  getUnitMeterHistory,
  backend_url
} from "../../../utils/urls";
import Layout from "../component/layout";
import HandoverInspectionTab from "../unit_management/HandoverInspectionTab";

const CreateMoveInHandover = ({ isDialog = false, onSuccess }) => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const propertyManagerSigRef = useRef(null);
  const customerSigRef = useRef(null);
  const propertyManagerFileRef = useRef(null);
  const customerFileRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [existingHandovers, setExistingHandovers] = useState([]);
  // Comprehensive inventory management state
  const [showInspectionTab, setShowInspectionTab] = useState(false);
  const [facilityId, setFacilityId] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [defaultCurrency, setDefaultCurrency] = useState(null);
  const [currencies, setCurrencies] = useState([]);

  // Image viewer state
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedItemImages, setSelectedItemImages] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState("");

  // New attachment form state (name-first UX)
  const [newAttachment, setNewAttachment] = useState({
    name: '',
    file: null
  });
  const fileUploadRef = useRef(null);

  // Smart meter state
  const [waterMeterData, setWaterMeterData] = useState(null);
  const [powerMeterData, setPowerMeterData] = useState(null);
  const [meterDataLoading, setMeterDataLoading] = useState(false);
  const [previousReadings, setPreviousReadings] = useState({
    water: [],
    electricity: []
  });


  // Handover form data
  const [handoverData, setHandoverData] = useState({
    unitId: "",
    customerId: "",
    handoverDate: new Date(),
    items: [],
    meterReadings: {
      electricity: { reading: 0, imageUrl: "" },
      water: { reading: 0, imageUrl: "" },
      gas: { reading: 0, imageUrl: "" }
    },
    keysHandedOver: 0,
    notes: "",
    attachments: [],
    status: "Draft",
    signatures: {
      propertyManager: {
        signature: "",
        date: null
      },
      customer: {
        signature: "",
        date: null
      }
    }
  });

  // Fetch currencies for this facility
  const fetchCurrencies = async (facilityIdValue) => {
    try {
      const response = await makeRequest2(
        `${GET_CURRENCIES_URL}/${facilityIdValue}`,
        "GET"
      );

      if (response.success && response.data) {
        const currencyData = response.data.data || response.data;
        const currenciesArray = Array.isArray(currencyData)
          ? currencyData
          : [currencyData];

        setCurrencies(currenciesArray);

        // Find default currency
        const defaultCurr = currenciesArray.find(curr => curr.isDefaultCurrency);
        if (defaultCurr) {
          setDefaultCurrency(defaultCurr);
        } else if (currenciesArray.length > 0) {
          // Use first currency as fallback
          setDefaultCurrency(currenciesArray[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  // Fetch smart meter data for water
  const fetchWaterMeterData = async (unitId) => {
    try {
      setMeterDataLoading(true);
      const response = await makeRequest2(`${getUnitMeterHistory}/${facilityId}/${unitId}`, 'GET');

      if (response && response.success && response.data && response.data.meter) {
        setWaterMeterData(response.data.meter);

        // Auto-fill current reading if available
        if (response.data.meter.currentReading) {
          handleMeterReadingChange('water', 'reading', response.data.meter.currentReading);
        }

        toast.current.show({
          severity: 'success',
          summary: 'Smart Water Meter Found',
          detail: 'Water meter reading automatically fetched',
          life: 3000
        });
      } else {
        setWaterMeterData(null);
      }
    } catch (error) {
      console.error('Error fetching water meter data:', error);
      setWaterMeterData(null);
    } finally {
      setMeterDataLoading(false);
    }
  };

  // Fetch smart meter data for power
  const fetchPowerMeterData = async (unitId) => {
    try {
      setMeterDataLoading(true);
      const response = await makeRequest2(
        `/api/app/power_meter_management/controllers/power_meters/get_unit_meter/${unitId}`,
        'GET'
      );

      if (response && response.success && response.data.data) {
        setPowerMeterData(response.data.data);

        // Auto-fill current reading if available
        if (response.data.data.currentReading) {
          handleMeterReadingChange('electricity', 'reading', response.data.data.currentReading);
        }

        toast.current.show({
          severity: 'success',
          summary: 'Smart Power Meter Found',
          detail: 'Power meter reading automatically fetched',
          life: 3000
        });
      } else {
        setPowerMeterData(null);
      }
    } catch (error) {
      console.error('Error fetching power meter data:', error);
      setPowerMeterData(null);
    } finally {
      setMeterDataLoading(false);
    }
  };

  // Fetch previous readings from past handovers
  const fetchPreviousReadings = async (unitId) => {
    try {
      const response = await makeRequest2(`${get_all_handovers}/${facilityId}`, "GET");

      if (response.success && response.data) {
        let handoversList = [];

        if (response.data.data && Array.isArray(response.data.data)) {
          handoversList = response.data.data;
        } else if (Array.isArray(response.data)) {
          handoversList = response.data;
        } else if (response.data.handovers && Array.isArray(response.data.handovers)) {
          handoversList = response.data.handovers;
        }

        // Filter handovers for this unit
        const unitHandovers = handoversList.filter(handover => {
          const handoverUnitId = typeof handover.unitId === 'object' ? handover.unitId?._id : handover.unitId;
          return String(handoverUnitId) === String(unitId);
        });

        // Extract meter readings from past handovers
        const waterReadings = [];
        const electricityReadings = [];

        unitHandovers.forEach(handover => {
          if (handover.meterReadings) {
            if (handover.meterReadings.water && handover.meterReadings.water.reading) {
              waterReadings.push({
                reading: handover.meterReadings.water.reading,
                date: handover.handoverDate || handover.createdAt,
                type: handover.handoverType || 'Unknown'
              });
            }
            if (handover.meterReadings.electricity && handover.meterReadings.electricity.reading) {
              electricityReadings.push({
                reading: handover.meterReadings.electricity.reading,
                date: handover.handoverDate || handover.createdAt,
                type: handover.handoverType || 'Unknown'
              });
            }
          }
        });

        // Sort by date (most recent first)
        waterReadings.sort((a, b) => new Date(b.date) - new Date(a.date));
        electricityReadings.sort((a, b) => new Date(b.date) - new Date(a.date));

        setPreviousReadings({
          water: waterReadings.slice(0, 5), // Keep only last 5 readings
          electricity: electricityReadings.slice(0, 5)
        });
      }
    } catch (error) {
      console.error('Error fetching previous readings:', error);
    }
  };

  // Load initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const facilityIdValue = await getItem("selectedFacilityId");
        setFacilityId(facilityIdValue);

        if (!facilityIdValue) {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Facility ID not found',
            life: 3000
          });
          return;
        }

        const [unitsResponse, customersResponse, handoversResponse] = await Promise.all([
          makeRequest2(`${getUnitsForFacility}/${facilityIdValue}`, "GET"),
          makeRequest2(`${getCustomersURL}/${facilityIdValue}`, "GET"),
          makeRequest2(`${get_all_handovers}/${facilityIdValue}`, "GET"),
          fetchCurrencies(facilityIdValue)
        ]);

        // Process handovers first to filter units
        let moveInHandovers = [];
        if (handoversResponse.success && handoversResponse.data) {
          let handoversList = [];

          // Handle different response structures
          if (handoversResponse.data.data && Array.isArray(handoversResponse.data.data)) {
            handoversList = handoversResponse.data.data;
          } else if (Array.isArray(handoversResponse.data)) {
            handoversList = handoversResponse.data;
          } else if (handoversResponse.data.handovers && Array.isArray(handoversResponse.data.handovers)) {
            handoversList = handoversResponse.data.handovers;
          }

          // Filter for move-in handovers
          moveInHandovers = handoversList.filter(handover => {
            if (!handover || typeof handover !== 'object') return false;
            const handoverType = handover.handoverType || handover.type;
            if (!handoverType) return false;
            const typeStr = handoverType.toString().toLowerCase();
            return typeStr.includes('movein') || typeStr.includes('move-in') || typeStr.includes('move_in');
          });

          console.log('Loaded existing move-in handovers:', moveInHandovers.length);
          setExistingHandovers(moveInHandovers);
        }

        if (unitsResponse.success) {
          // For move-in handovers, we want units with tenants/residents
          const unitsWithTenants = unitsResponse.data.filter(unit =>
            unit.tenantId || unit.residentId
          );

          // Filter out units that already have a completed move-in handover
          const availableUnits = unitsWithTenants.filter(unit => {
            const tenantId = unit.tenantId || unit.residentId;
            if (!tenantId) return true; // Keep units without tenants (shouldn't happen due to previous filter)

            // Check if this unit has a completed move-in handover
            const hasCompletedMoveIn = moveInHandovers.some(handover => {
              if (!handover || typeof handover !== 'object') return false;

              try {
                const handoverUnitId = typeof handover.unitId === 'object' ? handover.unitId?._id : handover.unitId;
                const handoverCustomerId = typeof handover.customerId === 'object' ? handover.customerId?._id : handover.customerId;

                const unitMatch = String(handoverUnitId) === String(unit._id);
                const customerMatch = String(handoverCustomerId) === String(tenantId);
                const isCompleted = handover.status === 'Completed';

                return unitMatch && customerMatch && isCompleted;
              } catch (error) {
                console.error('Error checking handover for unit:', error);
                return false;
              }
            });

            return !hasCompletedMoveIn; // Only include units WITHOUT completed move-in handovers
          });

          console.log(`Filtered units: ${unitsWithTenants.length} with tenants -> ${availableUnits.length} available for move-in`);
          setUnits(availableUnits);
        }

        if (customersResponse.success) {
          const activeCustomers = customersResponse.data.filter(customer =>
            customer.status === 'Active'
          );
          setCustomers(activeCustomers);
        }
      } catch (error) {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Error loading initial data',
          life: 3000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Render handover header


  // Event handlers
  const handleInputChange = (field, value) => {
    if (field === 'unitId' && value) {
      // Auto-select tenant when unit is selected
      const selectedUnit = units.find(unit => unit._id === value);
      if (selectedUnit) {
        const tenantId = selectedUnit.tenantId || selectedUnit.residentId;
        if (tenantId) {
          // Check if there's already an incomplete move-in handover for this unit and customer
          const hasIncompleteHandover = existingHandovers.some(handover => {
            if (!handover || typeof handover !== 'object') return false;

            try {
              // Handle both string IDs and populated objects
              const handoverUnitId = typeof handover.unitId === 'object' ? handover.unitId?._id : handover.unitId;
              const handoverCustomerId = typeof handover.customerId === 'object' ? handover.customerId?._id : handover.customerId;

              const unitMatch = String(handoverUnitId) === String(value);
              const customerMatch = String(handoverCustomerId) === String(tenantId);
              const isIncomplete = handover.status !== 'Completed';

              return unitMatch && customerMatch && isIncomplete;
            } catch (error) {
              console.error('Error checking existing handover:', error);
              return false;
            }
          });

          if (hasIncompleteHandover) {
            toast.current.show({
              severity: 'error',
              summary: 'Incomplete Move-In Handover Exists',
              detail: 'This unit already has an incomplete move-in handover. Please complete the existing handover before creating a new one.',
              life: 5000
            });
            // Don't proceed with unit selection
            return;
          }

          // Note: Units with completed move-in handovers are already filtered out from the dropdown
          // So we don't need to check again here. This block is kept for incomplete handovers only.

          setHandoverData(prev => ({
            ...prev,
            [field]: value,
            customerId: tenantId
          }));

          // Auto-load inspection items for this unit
          loadUnitInspectionItems(value);

          // Fetch smart meter data for this unit
          fetchWaterMeterData(value);
          fetchPowerMeterData(value);
          fetchPreviousReadings(value);

          return;
        } else {
          toast.current.show({
            severity: 'error',
            summary: 'No Tenant Assigned',
            detail: 'This unit does not have a tenant assigned. Move-in handovers can only be created for units with tenants.',
            life: 4000
          });
          return;
        }
      }
    }

    setHandoverData(prev => ({ ...prev, [field]: value }));
  };

  // Load inspection items automatically when unit is selected
  const loadUnitInspectionItems = async (unitId) => {
    try {
      console.log("Loading inspection items for unit:", unitId);

      // Load unit-specific inspection items
      const response = await makeRequest2(
        `${get_all_inspection_settings}/${facilityId}`,
        "GET"
      );

      console.log("Inspection items API response:", response);

      if (response.success && response.data) {
        // Ensure data is an array
        const allItems = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);

        console.log("All inspection items:", allItems);

        // Filter for unit-specific items only (matching HandoverInspectionTab logic)
        const unitSpecificItems = allItems.filter(item => {
          // Skip inactive items
          if (item.active === false) return false;

          // Check if item has unitId
          if (!item.unitId) return false;

          // Handle cases where unitId might be an object (populated) or a string
          const itemUnitId = typeof item.unitId === 'object' && item.unitId !== null
            ? item.unitId._id
            : item.unitId;

          return itemUnitId === unitId;
        });

        console.log("Unit-specific items found:", unitSpecificItems);

        if (unitSpecificItems.length === 0) {
          toast.current.show({
            severity: 'info',
            summary: 'No Unit-Specific Items',
            detail: 'No inspection items have been configured for this unit yet. You can add custom items or configure inspection items in Unit Management.',
            life: 4000
          });
          return;
        }

        // Convert inspection items to handover items format
        const handoverItems = unitSpecificItems.map(item => ({
          name: item.name,
          category: item.category,
          description: item.description || '',
          condition: item.defaultCondition || 'Good',
          quantity: item.defaultQuantity || 1,
          cost: item.cost || 0,
          currencyId: item.currencyId || null, // Preserve currency information
          serialNumber: '',
          notes: '',
          images: item.images || [],
          inspectionItemId: item._id // Keep reference to original inspection item
        }));

        // Add items to handover data (don't duplicate existing items)
        setHandoverData(prev => ({
          ...prev,
          items: [...prev.items, ...handoverItems]
        }));

        toast.current.show({
          severity: 'success',
          summary: 'Unit Inspection Items Loaded',
          detail: `${handoverItems.length} inspection item(s) automatically loaded for this unit`,
          life: 3000
        });
      } else {
        console.error("Failed to fetch inspection items:", response);
        toast.current.show({
          severity: 'warn',
          summary: 'Warning',
          detail: response?.error || 'Could not load inspection items for this unit',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error loading unit inspection items:', error);
      toast.current.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Could not load inspection items for this unit',
        life: 3000
      });
    }
  };

  const handleMeterReadingChange = (utility, field, value) => {
    setHandoverData(prev => ({
      ...prev,
      meterReadings: {
        ...prev.meterReadings,
        [utility]: {
          ...prev.meterReadings[utility],
          [field]: value
        }
      }
    }));
  };

  const archiveItem = (index) => {
    setHandoverData(prev => {
      const newItems = [...prev.items];
      // Mark item as archived instead of deleting
      newItems[index] = {
        ...newItems[index],
        isArchived: true,
        archivedAt: new Date().toISOString(),
        archivedBy: 'Current User'
      };

      return {
        ...prev,
        items: newItems.filter(item => !item.isArchived) // Hide archived items
      };
    });

    toast.current.show({
      severity: 'info',
      summary: 'Item Archived',
      detail: 'Item archived successfully. It will be kept in history.',
      life: 3000
    });
  };

  // Add attachment with name-first UX
  const handleAddAttachment = () => {
    if (!newAttachment.name || !newAttachment.name.trim()) {
      toast.current.show({
        severity: 'warning',
        summary: 'Name Required',
        detail: 'Please enter an attachment name',
        life: 3000
      });
      return;
    }
    if (!newAttachment.file) {
      toast.current.show({
        severity: 'warning',
        summary: 'File Required',
        detail: 'Please select a file to upload',
        life: 3000
      });
      return;
    }

    const attachment = {
      id: Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      name: newAttachment.name.trim(),
      fileName: newAttachment.file.name,
      fileUrl: URL.createObjectURL(newAttachment.file),
      file: newAttachment.file,
      uploadDate: new Date().toISOString(),
      size: newAttachment.file.size
    };

    setHandoverData(prev => ({
      ...prev,
      attachments: [...prev.attachments, attachment]
    }));

    // Reset form
    setNewAttachment({ name: '', file: null });
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }

    toast.current.show({
      severity: 'success',
      summary: 'Attachment Added',
      detail: 'Attachment added successfully',
      life: 3000
    });
  };

  // Update attachment name (inline editing)
  const updateAttachmentName = (index, name) => {
    setHandoverData(prev => {
      const newAttachments = [...prev.attachments];
      newAttachments[index] = {
        ...newAttachments[index],
        name: name
      };
      return { ...prev, attachments: newAttachments };
    });
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setHandoverData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));

    toast.current.show({
      severity: 'info',
      summary: 'Attachment Removed',
      detail: 'Attachment removed successfully',
      life: 3000
    });
  };

  const handleClear = () => {
    setHandoverData({
      unitId: "",
      customerId: "",
      handoverDate: new Date(),
      items: [],
      meterReadings: {
        electricity: { reading: 0, imageUrl: "" },
        water: { reading: 0, imageUrl: "" },
        gas: { reading: 0, imageUrl: "" }
      },
      keysHandedOver: 0,
      notes: "",
      attachments: [],
      status: "Draft",
      signatures: {
        propertyManager: {
          signature: "",
          date: null
        },
        customer: {
          signature: "",
          date: null
        }
      }
    });

    // Clear signature pads
    if (propertyManagerSigRef.current) {
      propertyManagerSigRef.current.clear();
    }
    if (customerSigRef.current) {
      customerSigRef.current.clear();
    }

    // Clear file upload refs
    if (propertyManagerFileRef.current) {
      propertyManagerFileRef.current.clear();
    }
    if (customerFileRef.current) {
      customerFileRef.current.clear();
    }

    setActiveTab(0);
  };

  const validateForm = () => {
    if (!handoverData.unitId) {
      toast.current.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a unit',
        life: 3000
      });
      return false;
    }

    if (!handoverData.customerId) {
      toast.current.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a customer',
        life: 3000
      });
      return false;
    }

    // Validate signatures only if status is 'Completed'
    if (handoverData.status === 'Completed') {
      if (!handoverData.signatures.propertyManager.signature) {
        toast.current.show({
          severity: 'error',
          summary: 'Signature Required',
          detail: 'Property Manager signature is required for completed handovers',
          life: 3000
        });
        setActiveTab(4); // Switch to signatures tab
        return false;
      }

      if (!handoverData.signatures.customer.signature) {
        toast.current.show({
          severity: 'error',
          summary: 'Signature Required',
          detail: 'Customer signature is required for completed handovers',
          life: 3000
        });
        setActiveTab(4); // Switch to signatures tab
        return false;
      }
    }

    return true;
  };

  // Signature handlers
  const handleSavePropertyManagerSignature = () => {
    if (propertyManagerSigRef.current && !propertyManagerSigRef.current.isEmpty()) {
      const signatureData = propertyManagerSigRef.current.toDataURL();
      setHandoverData(prev => ({
        ...prev,
        signatures: {
          ...prev.signatures,
          propertyManager: {
            signature: signatureData,
            date: new Date()
          }
        }
      }));
      toast.current.show({
        severity: 'success',
        summary: 'Signature Saved',
        detail: 'Property Manager signature saved successfully',
        life: 2000
      });
    } else {
      toast.current.show({
        severity: 'warn',
        summary: 'Empty Signature',
        detail: 'Please sign before saving',
        life: 2000
      });
    }
  };

  const handleClearPropertyManagerSignature = () => {
    if (propertyManagerSigRef.current) {
      propertyManagerSigRef.current.clear();
    }
    if (propertyManagerFileRef.current) {
      propertyManagerFileRef.current.clear();
    }
    setHandoverData(prev => ({
      ...prev,
      signatures: {
        ...prev.signatures,
        propertyManager: {
          signature: "",
          date: null
        }
      }
    }));
  };

  const handleSaveCustomerSignature = () => {
    if (customerSigRef.current && !customerSigRef.current.isEmpty()) {
      const signatureData = customerSigRef.current.toDataURL();
      setHandoverData(prev => ({
        ...prev,
        signatures: {
          ...prev.signatures,
          customer: {
            ...prev.signatures.customer,
            signature: signatureData,
            date: new Date()
          }
        }
      }));
      toast.current.show({
        severity: 'success',
        summary: 'Signature Saved',
        detail: 'Customer signature saved successfully',
        life: 2000
      });
    } else {
      toast.current.show({
        severity: 'warn',
        summary: 'Empty Signature',
        detail: 'Please sign before saving',
        life: 2000
      });
    }
  };

  const handleClearCustomerSignature = () => {
    if (customerSigRef.current) {
      customerSigRef.current.clear();
    }
    if (customerFileRef.current) {
      customerFileRef.current.clear();
    }
    setHandoverData(prev => ({
      ...prev,
      signatures: {
        ...prev.signatures,
        customer: {
          signature: "",
          date: null
        }
      }
    }));
  };

  // Image upload handlers for signatures
  const handlePropertyManagerImageUpload = (e) => {
    if (e.files && e.files[0]) {
      const file = e.files[0];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.current.show({
          severity: 'error',
          summary: 'Invalid File',
          detail: 'Please upload an image file',
          life: 3000
        });
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        setHandoverData(prev => ({
          ...prev,
          signatures: {
            ...prev.signatures,
            propertyManager: {
              signature: event.target.result,
              date: new Date()
            }
          }
        }));

        // Clear the canvas if it has any drawing
        if (propertyManagerSigRef.current) {
          propertyManagerSigRef.current.clear();
        }

        toast.current.show({
          severity: 'success',
          summary: 'Image Uploaded',
          detail: 'Property Manager signature image uploaded successfully',
          life: 2000
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCustomerImageUpload = (e) => {
    if (e.files && e.files[0]) {
      const file = e.files[0];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.current.show({
          severity: 'error',
          summary: 'Invalid File',
          detail: 'Please upload an image file',
          life: 3000
        });
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        setHandoverData(prev => ({
          ...prev,
          signatures: {
            ...prev.signatures,
            customer: {
              ...prev.signatures.customer,
              signature: event.target.result,
              date: new Date()
            }
          }
        }));

        // Clear the canvas if it has any drawing
        if (customerSigRef.current) {
          customerSigRef.current.clear();
        }

        toast.current.show({
          severity: 'success',
          summary: 'Image Uploaded',
          detail: 'Customer signature image uploaded successfully',
          life: 2000
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to upload images for an item
  const uploadItemImages = async (images, handoverId) => {
    if (!images || images.length === 0) return [];

    try {
      // Filter only images that need to be uploaded (have 'file' property)
      const imagesToUpload = images.filter(img => img.file);

      if (imagesToUpload.length === 0) {
        // Return existing URLs if no new images to upload
        return images.map(img => typeof img === 'string' ? img : img.preview || img.url);
      }

      const formData = new FormData();
      imagesToUpload.forEach((imageData, index) => {
        formData.append('images', imageData.file, imageData.name);
      });

      const response = await makeRequest2(
        `${upload_handover_images}/${facilityId}/${handoverId}`,
        "POST",
        formData,
        {
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.success && response.data?.images) {
        // Combine existing URLs with newly uploaded URLs
        const existingUrls = images.filter(img => !img.file).map(img =>
          typeof img === 'string' ? img : img.preview || img.url
        );
        const newUrls = response.data.images.map(img => img.url);

        return [...existingUrls, ...newUrls];
      } else {
        console.error('Image upload failed:', response?.error);
        throw new Error(response?.error || 'Image upload failed');
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  };

  const uploadAttachments = async (attachments, handoverId) => {
    if (!attachments || attachments.length === 0) return [];

    try {
      // Upload attachments with files
      const attachmentsToUpload = attachments.filter(att => att.file);

      if (attachmentsToUpload.length === 0) {
        // No new attachments to upload, return existing URLs
        return attachments.map(att => ({
          name: att.name,
          fileName: att.fileName,
          fileUrl: att.fileUrl,
          uploadDate: att.uploadDate
        }));
      }

      const formData = new FormData();
      attachmentsToUpload.forEach((attachment) => {
        formData.append('images', attachment.file, attachment.fileName || attachment.file.name);
      });

      const response = await makeRequest2(
        `${upload_handover_images}/${facilityId}/${handoverId}`,
        "POST",
        formData,
        {
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.success && response.data?.images) {
        // Map uploaded files to attachments with proper URLs
        const uploadedAttachments = attachmentsToUpload.map((att, index) => ({
          name: att.name,
          fileName: att.fileName,
          fileUrl: response.data.images[index]?.url || att.fileUrl,
          uploadDate: att.uploadDate
        }));

        // Combine with existing attachments (if any)
        const existingAttachments = attachments
          .filter(att => !att.file)
          .map(att => ({
            name: att.name,
            fileName: att.fileName,
            fileUrl: att.fileUrl,
            uploadDate: att.uploadDate
          }));

        return [...existingAttachments, ...uploadedAttachments];
      } else {
        console.error('Attachment upload failed:', response?.error);
        throw new Error(response?.error || 'Attachment upload failed');
      }
    } catch (error) {
      console.error('Error uploading attachments:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm() || isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      // Step 1: Create handover without images first
      const cleanedItems = handoverData.items.map(item => {
        const cleanedItem = { ...item };
        
        // Remove manual IDs (numeric IDs that cause ObjectId cast errors)
        if (typeof cleanedItem._id === 'number' || typeof cleanedItem.id === 'number') {
          delete cleanedItem._id;
          delete cleanedItem.id;
        }
        
        // Create without images initially
        cleanedItem.images = [];
        
        return cleanedItem;
      });

      // Validate attachment names
      const attachmentsWithoutNames = handoverData.attachments.filter(att => !att.name || att.name.trim() === '');
      if (attachmentsWithoutNames.length > 0) {
        toast.current.show({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please provide a name for all attachments',
          life: 3000
        });
        setIsSubmitting(false);
        return;
      }

      // Clean attachments - remove file objects, keep only metadata
      const cleanedAttachments = handoverData.attachments.map(att => ({
        name: att.name,
        fileName: att.fileName,
        fileUrl: att.fileUrl,
        uploadDate: att.uploadDate
      }));

      const formData = {
        ...handoverData,
        handoverDate: handoverData.handoverDate.toISOString(),
        items: cleanedItems,
        attachments: cleanedAttachments
      };

      // Create the handover first
      const response = await makeRequest2(
        `${create_move_in_handover}/${facilityId}`,
        "POST",
        formData
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create move-in handover');
      }

      const createdHandoverId = response.data?._id;
      
      // Step 2: Upload images for items that have them
      if (createdHandoverId && handoverData.items.some(item => item.images && item.images.length > 0)) {
        try {
          // Upload images and update items with URLs
          const itemsWithImageUrls = await Promise.all(
            handoverData.items.map(async (item) => {
              const cleanedItem = { ...item };
              
              // Remove manual IDs
              if (typeof cleanedItem._id === 'number' || typeof cleanedItem.id === 'number') {
                delete cleanedItem._id;
                delete cleanedItem.id;
              }
              
              // Upload images if they exist
              try {
                if (item.images && item.images.length > 0) {
                  cleanedItem.images = await uploadItemImages(item.images, createdHandoverId);
                } else {
                  cleanedItem.images = [];
                }
              } catch (imageError) {
                console.error('Error uploading images for item:', item.name, imageError);
                cleanedItem.images = [];
              }
              
              return cleanedItem;
            })
          );

          // Step 3: Update the handover with image URLs
          const updatePayload = {
            items: itemsWithImageUrls
          };

          await makeRequest2(
            `${update_handover}/${facilityId}/${createdHandoverId}`,
            "PUT",
            updatePayload
          );
        } catch (imageError) {
          console.error('Error uploading images:', imageError);
        }
      }

      // Step 4: Upload attachments if there are any
      if (createdHandoverId && handoverData.attachments.length > 0) {
        try {
          const attachmentsWithUrls = await uploadAttachments(handoverData.attachments, createdHandoverId);

          // Update handover with actual attachment URLs
          const attachmentUpdatePayload = {
            attachments: attachmentsWithUrls
          };

          await makeRequest2(
            `${update_handover}/${facilityId}/${createdHandoverId}`,
            "PUT",
            attachmentUpdatePayload
          );
        } catch (attachmentError) {
          console.error('Error uploading attachments:', attachmentError);
        }
      }

      // Show success message
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Move-in handover created successfully',
        life: 3000
      });

      // Navigate or close dialog
      if (isDialog) {
        onSuccess?.();
      } else {
        navigate("/facility/handover_management/handovers", {
          state: { activeTab: 'moveIn' },
          replace: true
        });
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An unexpected error occurred',
        life: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tab render functions
  const renderBasicInfoTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-info-circle text-primary me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Basic Information</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        <div className="row">
          <div className="col-md-6">
            <label className="fw-medium mb-2">Unit *</label>
            <Dropdown
              value={handoverData.unitId}
              options={units.map(unit => ({
                label: `${unit.name} - ${unit.floorUnitNo}`,
                value: unit._id
              }))}
              onChange={(e) => handleInputChange('unitId', e.value)}
              placeholder="Select a unit"
              filter
              className="w-100"
            />
          </div>
          <div className="col-md-6">
            <label className="fw-medium mb-2">Customer *</label>
            <Dropdown
              value={handoverData.customerId}
              options={customers.map(customer => ({
                label: `${customer.firstName} ${customer.lastName}`,
                value: customer._id
              }))}
              onChange={(e) => handleInputChange('customerId', e.value)}
              placeholder="Select a customer"
              filter
              className="w-100"
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-6">
            <label className="fw-medium mb-2">Handover Date *</label>
            <Calendar
              value={handoverData.handoverDate}
              onChange={(e) => handleInputChange('handoverDate', e.value)}
              showIcon
              dateFormat="dd/mm/yy"
              className="w-100"
            />
          </div>
          <div className="col-md-6">
            <label className="fw-medium mb-2">Status</label>
            <Dropdown
              value={handoverData.status}
              options={[
                { label: 'Draft', value: 'Draft' },
                { label: 'Completed', value: 'Completed' }
              ]}
              onChange={(e) => handleInputChange('status', e.value)}
              className="w-100"
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-6">
            <label className="fw-medium mb-2">Number of Keys</label>
            <InputNumber
              value={handoverData.keysHandedOver}
              onValueChange={(e) => handleInputChange('keysHandedOver', e.value || 0)}
              min={0}
              showButtons
              className="w-100"
            />
          </div>
        </div>
      </div>
    </Card>
  );

  const renderMeterReadingsTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-gauge text-purple me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Meter Readings</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        <div className="row">
          <div className="col-md-4">
            <label className="fw-medium mb-2">Electricity Reading</label>
            <div className="d-flex gap-2">
              <InputNumber
                value={handoverData.meterReadings.electricity.reading}
                onValueChange={(e) => handleMeterReadingChange('electricity', 'reading', e.value || 0)}
                min={0}
                showButtons
                className="w-100"
              />
              {powerMeterData && (
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-sm"
                  tooltip="Fetch Latest Reading"
                  onClick={() => fetchPowerMeterData(handoverData.unitId)}
                  disabled={!handoverData.unitId || meterDataLoading}
                />
              )}
            </div>
            {powerMeterData && (
              <small className="text-success">
                Smart meter: {powerMeterData.meterNumber || 'Connected'}
              </small>
            )}
          </div>
          <div className="col-md-4">
            <label className="fw-medium mb-2">Water Reading</label>
            <div className="d-flex gap-2">
              <InputNumber
                value={handoverData.meterReadings.water.reading}
                onValueChange={(e) => handleMeterReadingChange('water', 'reading', e.value || 0)}
                min={0}
                showButtons
                className="w-100"
              />
              {waterMeterData && (
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-sm"
                  tooltip="Fetch Latest Reading"
                  onClick={() => fetchWaterMeterData(handoverData.unitId)}
                  disabled={!handoverData.unitId || meterDataLoading}
                />
              )}
            </div>
            {waterMeterData && (
              <small className="text-success">
                Smart meter: {waterMeterData.meterNumber}
              </small>
            )}
          </div>
          <div className="col-md-4">
            <label className="fw-medium mb-2">Gas Reading</label>
            <InputNumber
              value={handoverData.meterReadings.gas.reading}
              onValueChange={(e) => handleMeterReadingChange('gas', 'reading', e.value || 0)}
              min={0}
              showButtons
              className="w-100"
            />
            <small className="text-muted">Manual entry only</small>
          </div>
        </div>

        {/* Previous Readings Section */}
        {(previousReadings.water.length > 0 || previousReadings.electricity.length > 0) && (
          <div className="mt-4">
            <h6 className="fw-medium mb-3">Previous Readings</h6>
            <div className="row">
              {previousReadings.electricity.length > 0 && (
                <div className="col-md-6">
                  <h6 className="fw-medium mb-2">Electricity</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reading</th>
                          <th>Date</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousReadings.electricity.map((reading, index) => (
                          <tr key={index}>
                            <td>{reading.reading} kWh</td>
                            <td>{new Date(reading.date).toLocaleDateString()}</td>
                            <td>
                              <Tag
                                severity={reading.type.toLowerCase().includes('in') ? 'success' : 'info'}
                                value={reading.type}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {previousReadings.water.length > 0 && (
                <div className="col-md-6">
                  <h6 className="fw-medium mb-2">Water</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reading</th>
                          <th>Date</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousReadings.water.map((reading, index) => (
                          <tr key={index}>
                            <td>{reading.reading} m³</td>
                            <td>{new Date(reading.date).toLocaleDateString()}</td>
                            <td>
                              <Tag
                                severity={reading.type.toLowerCase().includes('in') ? 'success' : 'info'}
                                value={reading.type}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  const renderInventoryTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-package text-orange me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Inventory Items ({handoverData.items?.length || 0})</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        {handoverData.items.length === 0 && (
          <div className="alert alert-info mb-3">
            <i className="ti ti-info-circle me-2"></i>
            Inventory items are automatically loaded when you select a unit. Items are managed in <strong>Unit Management → Handover Inspection</strong>.
          </div>
        )}
        <DataTable
          value={handoverData.items}
          emptyMessage="No items added yet"
          responsiveLayout="scroll"
          className="mb-4"
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 15]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords} items"
        >
          <Column field="name" header="Name" />
          <Column field="category" header="Category" />
          <Column
            field="condition"
            header="Condition"
            body={(rowData) => (
              <Tag
                value={rowData.condition}
                severity={
                  rowData.condition === 'Excellent' || rowData.condition === 'Good' ? 'success' :
                    rowData.condition === 'Fair' ? 'warning' : 'danger'
                }
              />
            )}
          />
          <Column field="quantity" header="Quantity" />
          <Column
            field="cost"
            header="Cost"
            body={(rowData) => {
              if (!rowData.cost || parseFloat(rowData.cost) === 0) {
                return <span>N/A</span>;
              }

              // Find the currency for this item
              let itemCurrency = defaultCurrency; // Default fallback

              if (rowData.currencyId) {
                // If currencyId is populated (object), use it directly
                if (typeof rowData.currencyId === 'object' && rowData.currencyId !== null && rowData.currencyId.currencyShortCode) {
                  itemCurrency = rowData.currencyId;
                } else if (typeof rowData.currencyId === 'string') {
                  // If currencyId is just an ID, find it in currencies array
                  const foundCurrency = currencies.find(curr => curr._id === rowData.currencyId);
                  if (foundCurrency) {
                    itemCurrency = foundCurrency;
                  }
                }
              }

              const currencyCode = itemCurrency?.currencyShortCode || '$';
              return <span>{currencyCode}{parseFloat(rowData.cost).toFixed(2)}</span>;
            }}
          />
          <Column
            header="Images"
            body={(rowData) => (
              <div>
                {rowData.images && rowData.images.length > 0 ? (
                  <Button
                    label={`${rowData.images.length}`}
                    icon="ti ti-photo"
                    className="p-button-sm p-button-info"
                    onClick={() => {
                      setSelectedItemImages(rowData.images);
                      setSelectedItemName(rowData.name);
                      setShowImageDialog(true);
                    }}
                    tooltip="View images"
                  />
                ) : (
                  <Tag value="No images" severity="secondary" />
                )}
              </div>
            )}
          />
          <Column
            body={(rowData, options) => (
              <Button
                icon="ti ti-archive"
                className="p-button-rounded p-button-text p-button-warning p-button-sm"
                onClick={() => archiveItem(options.rowIndex)}
                tooltip="Archive item (keeps history)"
              />
            )}
            header="Actions"
          />
        </DataTable>

        <div className="mt-4">
          <h6 className="fw-medium mb-3">Additional Notes</h6>
          <InputTextarea
            value={handoverData.notes}
            onChange={(e) => setHandoverData({ ...handoverData, notes: e.target.value })}
            rows={4}
            className="w-100"
            placeholder="Enter any additional notes about the handover..."
          />
        </div>
      </div>
    </Card>
  );

  const renderAttachmentsTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-paperclip text-blue me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Attachments ({handoverData.attachments?.length || 0})</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        {/* Add New Attachment Form */}
        <div className="border rounded p-3 mb-4 bg-light">
          <h6 className="fw-medium mb-3">Add New Attachment</h6>
          <div className="row g-3">
            <div className="col-md-5">
              <label className="form-label fw-medium">
                Attachment Name <span className="text-danger">*</span>
              </label>
              <InputText
                value={newAttachment.name}
                onChange={(e) => setNewAttachment(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Kitchen Photo, Living Room Image"
                className="w-100"
              />
            </div>
            <div className="col-md-5">
              <label className="form-label fw-medium">
                Select File <span className="text-danger">*</span>
              </label>
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                name="attachment"
                accept="image/*,application/pdf"
                maxFileSize={5000000}
                onSelect={(e) => {
                  if (e.files && e.files[0]) {
                    setNewAttachment(prev => ({ ...prev, file: e.files[0] }));
                  }
                }}
                chooseLabel={newAttachment.file ? newAttachment.file.name : "Choose File"}
                className="w-100"
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <Button
                label="Add"
                icon="ti ti-plus"
                onClick={handleAddAttachment}
                className="p-button-success w-100"
              />
            </div>
          </div>
        </div>

        {/* Attachments List */}
        {handoverData.attachments.length > 0 ? (
          <div>
            <h6 className="fw-medium mb-3">Attachments List</h6>
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '5%' }}></th>
                  <th style={{ width: '35%' }}>Name/Label</th>
                  <th style={{ width: '30%' }}>File Name</th>
                  <th style={{ width: '15%' }}>Date</th>
                  <th style={{ width: '15%' }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {handoverData.attachments.map((attachment, index) => (
                  <tr key={index}>
                    <td>
                      <i className={`ti ${attachment.fileName?.toLowerCase().endsWith('.pdf') ? 'ti-file-type-pdf text-danger' :
                          attachment.fileName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? 'ti-photo text-primary' :
                            'ti-file text-secondary'
                        }`} style={{ fontSize: '1.5rem' }}></i>
                    </td>
                    <td>
                      <InputText
                        value={attachment.name || ''}
                        onChange={(e) => updateAttachmentName(index, e.target.value)}
                        placeholder="Enter name"
                        className="w-100"
                        size="small"
                      />
                    </td>
                    <td>
                      <small className="text-muted">{attachment.fileName || 'No filename'}</small>
                    </td>
                    <td>
                      <small className="text-muted">
                        {attachment.uploadDate ? new Date(attachment.uploadDate).toLocaleDateString() : 'N/A'}
                      </small>
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        {attachment.fileUrl && (
                          <Button
                            icon="ti ti-eye"
                            className="p-button-sm p-button-text p-button-info"
                            onClick={() => window.open(attachment.fileUrl, '_blank')}
                            tooltip="View"
                          />
                        )}
                        <Button
                          icon="ti ti-trash"
                          className="p-button-sm p-button-text p-button-danger"
                          onClick={() => removeAttachment(index)}
                          tooltip="Remove"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-muted py-4">
            <i className="ti ti-paperclip" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
            <p className="mt-2 mb-0">No attachments added yet</p>
            <small>Add your first attachment using the form above</small>
          </div>
        )}
      </div>
    </Card>
  );

  const renderSignaturesTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-writing text-success me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Signatures</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        <div className="alert alert-info mb-4">
          <i className="ti ti-info-circle me-2"></i>
          <strong>Note:</strong> Signatures are required when handover status is set to 'Completed'. You can either draw a signature or upload an image.
        </div>

        {/* Property Manager Signature Section */}
        <div className="border rounded p-4 mb-4">
          <h6 className="fw-medium mb-3">
            <i className="ti ti-user-shield me-2 text-primary"></i>
            Property Manager Signature {handoverData.status === 'Completed' && <span className="text-danger">*</span>}
          </h6>

          {/* Draw Signature */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Draw Signature</label>
            <div className="signature-canvas-container" style={{ border: '2px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
              <SignatureCanvas
                ref={propertyManagerSigRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
              />
            </div>
            <div className="d-flex gap-2 mt-2">
              <Button
                label="Clear Canvas"
                icon="ti ti-eraser"
                className="p-button-outlined p-button-secondary p-button-sm"
                onClick={() => {
                  if (propertyManagerSigRef.current) {
                    propertyManagerSigRef.current.clear();
                  }
                }}
              />
              <Button
                label="Save Drawn Signature"
                icon="ti ti-check"
                className="p-button-success p-button-sm"
                onClick={handleSavePropertyManagerSignature}
              />
            </div>
          </div>

          {/* Upload Signature Image */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Or Upload Signature Image</label>
            <FileUpload
              ref={propertyManagerFileRef}
              mode="basic"
              name="propertyManagerSignature"
              accept="image/*"
              maxFileSize={2000000}
              onSelect={handlePropertyManagerImageUpload}
              chooseLabel="Choose Image"
              className="w-100"
              auto
            />
            <small className="text-muted">Max file size: 2MB. Supported formats: JPG, PNG, GIF</small>
          </div>

          {/* Status Display */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              {handoverData.signatures.propertyManager.signature && (
                <div className="text-success">
                  <i className="ti ti-check-circle me-1"></i>
                  Signature saved on {handoverData.signatures.propertyManager.date && new Date(handoverData.signatures.propertyManager.date).toLocaleString()}
                </div>
              )}
            </div>
            <div>
              {handoverData.signatures.propertyManager.signature && (
                <Button
                  label="Remove Signature"
                  icon="ti ti-trash"
                  className="p-button-outlined p-button-danger p-button-sm"
                  onClick={handleClearPropertyManagerSignature}
                />
              )}
            </div>
          </div>

          {/* Saved Signature Preview */}
          {handoverData.signatures.propertyManager.signature && (
            <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <small className="text-muted d-block mb-2 fw-medium">Saved Signature:</small>
              <div className="bg-white p-2 rounded" style={{ border: '1px solid #dee2e6' }}>
                <img
                  src={handoverData.signatures.propertyManager.signature}
                  alt="Property Manager Signature"
                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px', display: 'block', margin: '0 auto' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Customer Signature Section */}
        <div className="border rounded p-4">
          <h6 className="fw-medium mb-3">
            <i className="ti ti-user me-2 text-primary"></i>
            Customer Signature {handoverData.status === 'Completed' && <span className="text-danger">*</span>}
          </h6>

          {/* Draw Signature */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Draw Signature</label>
            <div className="signature-canvas-container" style={{ border: '2px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
              <SignatureCanvas
                ref={customerSigRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
              />
            </div>
            <div className="d-flex gap-2 mt-2">
              <Button
                label="Clear Canvas"
                icon="ti ti-eraser"
                className="p-button-outlined p-button-secondary p-button-sm"
                onClick={() => {
                  if (customerSigRef.current) {
                    customerSigRef.current.clear();
                  }
                }}
              />
              <Button
                label="Save Drawn Signature"
                icon="ti ti-check"
                className="p-button-success p-button-sm"
                onClick={handleSaveCustomerSignature}
              />
            </div>
          </div>

          {/* Upload Signature Image */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Or Upload Signature Image</label>
            <FileUpload
              ref={customerFileRef}
              mode="basic"
              name="customerSignature"
              accept="image/*"
              maxFileSize={2000000}
              onSelect={handleCustomerImageUpload}
              chooseLabel="Choose Image"
              className="w-100"
              auto
            />
            <small className="text-muted">Max file size: 2MB. Supported formats: JPG, PNG, GIF</small>
          </div>

          {/* Status Display */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              {handoverData.signatures.customer.signature && (
                <div className="text-success">
                  <i className="ti ti-check-circle me-1"></i>
                  Signature saved on {handoverData.signatures.customer.date && new Date(handoverData.signatures.customer.date).toLocaleString()}
                </div>
              )}
            </div>
            <div>
              {handoverData.signatures.customer.signature && (
                <Button
                  label="Remove Signature"
                  icon="ti ti-trash"
                  className="p-button-outlined p-button-danger p-button-sm"
                  onClick={handleClearCustomerSignature}
                />
              )}
            </div>
          </div>

          {/* Saved Signature Preview */}
          {handoverData.signatures.customer.signature && (
            <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <small className="text-muted d-block mb-2 fw-medium">Saved Signature:</small>
              <div className="bg-white p-2 rounded" style={{ border: '1px solid #dee2e6' }}>
                <img
                  src={handoverData.signatures.customer.signature}
                  alt="Customer Signature"
                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px', display: 'block', margin: '0 auto' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );


  const content = (
    <>
      <Toast ref={toast} />

      {loading ? (
        <div className="d-flex align-items-center justify-content-center py-5">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".5s" />
          <span className="ms-3 font-medium">Loading data...</span>
        </div>
      ) : (
        <>


          {/* Enhanced Tab Interface */}
          <TabView
            activeIndex={activeTab}
            onTabChange={(e) => setActiveTab(e.index)}
            className="custom-tabview"
          >
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <i className="ti ti-info-circle me-2"></i>
                  Basic Info
                </div>
              }
            >
              {renderBasicInfoTab()}
            </TabPanel>
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <i className="ti ti-gauge me-2"></i>
                  Meter Readings
                </div>
              }
            >
              {renderMeterReadingsTab()}
            </TabPanel>
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <i className="ti ti-package me-2"></i>
                  Inventory ({handoverData.items?.length || 0})
                </div>
              }
            >
              {renderInventoryTab()}
            </TabPanel>
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <i className="ti ti-paperclip me-2"></i>
                  Attachments ({handoverData.attachments?.length || 0})
                </div>
              }
            >
              {renderAttachmentsTab()}
            </TabPanel>
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <i className="ti ti-writing me-2"></i>
                  Signatures
                  {handoverData.status === 'Completed' && <span className="text-danger ms-1">*</span>}
                </div>
              }
            >
              {renderSignaturesTab()}
            </TabPanel>
          </TabView>

          {/* Action Buttons */}
          <div className="d-flex justify-content-end align-items-center mt-4" style={{ gap: '0.75rem' }}>
            <Button
              type="button"
              label="Clear Form"
              icon="ti ti-eraser"
              className="p-button-outlined p-button-secondary"
              onClick={handleClear}
              disabled={isSubmitting}
            />
            <Button
              type="button"
              label={isSubmitting ? "Creating..." : "Create Move-In Handover"}
              icon="ti ti-check"
              className="p-button-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </div>
        </>
      )}

      {/* Dialogs */}
      {showInspectionTab && (
        <HandoverInspectionTab 
          facilityId={facilityId} 
          unitId={handoverData.unitId}
          showListView={false}
          autoOpenModal={true}
          context="moveIn"
          onItemAdded={(addedItem) => {
            console.log('Move-in handover: onItemAdded called with:', addedItem);
            
            // Add item to local handover inventory immediately
            setHandoverData(prev => {
              const newItems = [...prev.items, addedItem];
              console.log('Move-in handover: Updated items array:', newItems);
              return {
                ...prev,
                items: newItems
              };
            });
            
            // Show success message
            toast.current.show({
              severity: 'success',
              summary: 'Item Added',
              detail: `${addedItem.name} added to inventory`,
              life: 3000
            });
          }}
          onClose={() => {
            setShowInspectionTab(false);
            // Note: We don't reload inspection items here to avoid conflicts
            // The onItemAdded callback already handles adding the new item
          }}
        />
      )}

      {/* Image Viewer Dialog */}
      <Dialog
        header={`Images - ${selectedItemName}`}
        visible={showImageDialog}
        style={{ width: '80vw', maxWidth: '900px' }}
        onHide={() => setShowImageDialog(false)}
        dismissableMask
      >
        <div className="row g-3">
          {selectedItemImages.length > 0 ? (
            selectedItemImages.map((imageUrl, index) => (
              <div key={index} className="col-md-6">
                <div className="card">
                  <img
                    src={`${backend_url}${imageUrl}`}
                    alt={`${selectedItemName} - Image ${index + 1}`}
                    style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain' }}
                    className="card-img-top"
                  />
                  <div className="card-body">
                    <small className="text-muted">Image {index + 1}</small>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12 text-center py-4">
              <p className="text-muted">No images available</p>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );

  if (isDialog) {
    return content;
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/app/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/handover_management">Handover Management</Link>
                </li>
                <li className="breadcrumb-item">Create Move-In Handover</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="#" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          {content}
        </div>
      </div>
    </Layout>
  );
};

export default CreateMoveInHandover;